import React from "react";
import { FiShield } from "react-icons/fi";
import { Link } from "react-router-dom";
import { siteConfig } from "../Datas/productData";
import "../css/privacy.scss";

function Privacy() {
  const sections = [
    ["overview", "Overview"],
    ["information", "Information we collect"],
    ["purpose", "How we use information"],
    ["sharing", "When information is shared"],
    ["retention", "Retention and security"],
    ["rights", "Your privacy rights"],
    ["cookies", "Cookies"],
    ["contact-privacy", "Contact"],
  ];

  return (
    <main className="privacy-page">
      <header className="privacy-page__hero">
        <FiShield aria-hidden="true" />
        <p>Legal</p>
        <h1>Privacy policy</h1>
        <span>Last updated 16 September 2026</span>
      </header>

      <div className="privacy-page__layout">
        <aside aria-label="Privacy policy contents">
          <p>On this page</p>
          <nav>
            {sections.map(([id, label], index) => (
              <a href={`#${id}`} key={id}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                {label}
              </a>
            ))}
          </nav>
        </aside>

        <article>
          <section id="overview">
            <p className="privacy-page__section-number">01</p>
            <h2>Overview</h2>
            <p>
              This privacy policy explains how {siteConfig.displayName}, operated
              by {siteConfig.legalName}, collects, uses and protects personal
              information when you visit this storefront, place an order,
              contact customer care or sign up for product news.
            </p>
            <p>
              We process personal information responsibly, only for clear
              purposes and only for as long as it is needed. This policy should
              be read together with our <Link to="/terms">terms and conditions</Link>.
            </p>
          </section>

          <section id="information">
            <p className="privacy-page__section-number">02</p>
            <h2>Information we collect</h2>
            <p>Depending on how you use the store, we may receive:</p>
            <ul>
              <li>Contact details such as your name, email address, telephone number and delivery address.</li>
              <li>Order details including purchased items, preferences, payment status, returns and customer-care history.</li>
              <li>Technical data such as device type, browser, approximate location, IP address and storefront interactions.</li>
              <li>Marketing preferences and information you choose to include in a form, review or message.</li>
            </ul>
            <p>
              Payment credentials are handled by the payment provider selected
              at checkout. We do not store complete card numbers in the storefront.
            </p>
          </section>

          <section id="purpose">
            <p className="privacy-page__section-number">03</p>
            <h2>How we use information</h2>
            <p>We use information where it is necessary to:</p>
            <ul>
              <li>process, deliver and support an order or return;</li>
              <li>answer requests and provide product or service advice;</li>
              <li>operate, secure and improve our website and shopping experience;</li>
              <li>meet legal, tax, fraud-prevention and accounting obligations;</li>
              <li>send news or offers when you have chosen to receive them.</li>
            </ul>
            <p>
              Where consent is the legal basis, it may be withdrawn at any time.
              Withdrawing consent does not affect processing that was lawful before withdrawal.
            </p>
          </section>

          <section id="sharing">
            <p className="privacy-page__section-number">04</p>
            <h2>When information is shared</h2>
            <p>
              We share only the information needed by trusted service providers
              that help operate the store—for example payment, fulfilment,
              delivery, hosting, analytics and customer-service providers. They
              must protect it and may not use it for unrelated purposes.
            </p>
            <p>
              Information may also be disclosed when required by law, to protect
              customers and the store, or as part of a legitimate corporate
              transaction. We do not sell personal information.
            </p>
          </section>

          <section id="retention">
            <p className="privacy-page__section-number">05</p>
            <h2>Retention and security</h2>
            <p>
              Personal information is kept only for the period necessary for
              the purpose described above and any applicable statutory period.
              We use organisational and technical measures designed to prevent
              unauthorised access, alteration, loss or disclosure.
            </p>
            <p>
              No online system is completely risk-free. Keep account and device
              credentials private and contact us promptly if you believe an
              order or message has been accessed without permission.
            </p>
          </section>

          <section id="rights">
            <p className="privacy-page__section-number">06</p>
            <h2>Your privacy rights</h2>
            <p>
              Subject to local law, you may ask to access, correct, delete,
              restrict or receive a portable copy of personal information. You
              may also object to certain processing or lodge a complaint with
              your local data-protection authority.
            </p>
            <p>
              To protect you, we may need to verify your identity before acting
              on a request. We will explain if a legal exception applies.
            </p>
          </section>

          <section id="cookies">
            <p className="privacy-page__section-number">07</p>
            <h2>Cookies</h2>
            <p>
              Essential cookies keep the store secure and remember your bag.
              Optional analytics or personalisation technologies should be used
              only where permitted and in line with the preference selected in
              the cookie settings of your browser or the store.
            </p>
            <p>Read the full <Link to="/cookies">cookie policy</Link> for categories, retention and available controls.</p>
          </section>

          <section id="contact-privacy" className="privacy-page__contact">
            <p className="privacy-page__section-number">08</p>
            <h2>Questions about your data?</h2>
            <p>
              Contact our customer-care team and mark your request “Privacy”.
            </p>
            <a href={`mailto:${siteConfig.contact.email}`}>{siteConfig.contact.email}</a>
            <span>{siteConfig.legalName}</span>
            <span>{siteConfig.contact.location}</span>
          </section>
        </article>
      </div>
    </main>
  );
}

export default Privacy;
